import React from 'react';

import { Alert, DatePicker, Form, Input, message, Modal, Radio, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { ErrorCode } from '@app/models/error';
import { DeliveryScenario, Module } from '@app/models/users';
import { getResponseErrors, translateApiError } from '@app/services/apiErrors';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';
import { PartySearch } from '@components/daData';

import { useAddParticipantMutation } from '../../../../hooks';

import { ValidationSchema } from './data/validation';
import { IFormValues } from './interfaces';

const RELEASED_SCENARIOS = [DeliveryScenario.Matrix];
const ORBIS_INN = '7720464986';

const INN_ERROR_CODES: string[] = [
  ErrorCode.INVALID_INN,
  ErrorCode.PARTY_NOT_FOUND,
  ErrorCode.PARTY_NOT_ACTIVE,
  ErrorCode.PARTICIPATION_NOT_UNIQUE,
];

interface IProps {
  open: boolean;
  onClose: () => void;
}

export const AddParticipantModal: React.FC<IProps> = ({ open, onClose }) => {
  const [form] = Form.useForm<IFormValues>();
  const [formError, setFormError] = React.useState<string>();
  const spaceT = useTranslation(NSKeys.spaceSettings);
  const commonT = useTranslation(NSKeys.common);
  const validationT = useTranslation(NSKeys.validation);
  const getErrorMessage = useApiErrorMessage();
  const addParticipant = useAddParticipantMutation();
  const selectedModule = Form.useWatch('module', form);
  const selectedScenario = Form.useWatch('scenario', form);

  const isDelivery = selectedModule === Module.Delivery;
  const isOrbis = isDelivery && selectedScenario === DeliveryScenario.Matrix;

  React.useEffect(() => {
    if (open) {
      form.resetFields();
      setFormError(undefined);
    }
  }, [open]);

  const handleSubmit = (values: IFormValues) => {
    if (!values.module) {
      return;
    }
    setFormError(undefined);
    addParticipant.mutate(
      {
        module: values.module,
        inn: values.inn,
        contract_number: values.contract_number.trim(),
        contract_date: values.contract_date,
        scenario: isDelivery ? values.scenario : undefined,
      },
      {
        onSuccess: () => {
          message.success(spaceT.t('coalition.addSuccess'));
          onClose();
        },
        onError: (err) => {
          const innError = getResponseErrors(err).find((error) => INN_ERROR_CODES.includes(error.code));
          const { pointerErrors } = getFormErrorsFromResponse(values, err, validationT.t);
          if (innError) {
            form.setFields([{ name: 'inn', errors: [translateApiError(innError, validationT.t)] }]);
          } else if (pointerErrors.length) {
            form.setFields(pointerErrors);
          } else {
            setFormError(getErrorMessage(err));
          }
        },
      }
    );
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await ValidationSchema(validationT.t).validateSyncAt(field, { [field]: value });
    },
  };

  return (
    <Modal
      open={open}
      title={spaceT.t('coalition.addTitle')}
      okText={commonT.t('buttonAdd')}
      cancelText={commonT.t('buttonCancel')}
      onOk={() => form.submit()}
      onCancel={onClose}
      confirmLoading={addParticipant.isLoading}
      destroyOnHidden
    >
      <Form<IFormValues>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={addParticipant.isLoading}
        onValuesChange={(changed) => {
          if ('module' in changed) {
            form.resetFields(['scenario', 'inn']);
          }
          if (changed.scenario === DeliveryScenario.Matrix) {
            form.setFieldValue('inn', ORBIS_INN);
          }
        }}
      >
        <Form.Item<IFormValues> name="module" required label={spaceT.t('coalition.module')} rules={[yupSync]}>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            options={Object.values(Module).map((value) => ({
              value,
              label: commonT.t(`module.${value}` as const),
            }))}
          />
        </Form.Item>
        {isDelivery && (
          <Form.Item<IFormValues> name="scenario" required label={spaceT.t('coalition.scenario')} rules={[yupSync]}>
            <Radio.Group>
              <Space orientation="vertical">
                {Object.values(DeliveryScenario).map((value) => (
                  <Radio key={value} value={value} disabled={!RELEASED_SCENARIOS.includes(value)}>
                    {spaceT.t(`scenario.${value}` as const)}
                    {!RELEASED_SCENARIOS.includes(value) && (
                      <Typography.Text type="secondary"> · {commonT.t('hidden')}</Typography.Text>
                    )}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>
        )}
        <Form.Item<IFormValues> name="inn" required label={spaceT.t('coalition.inn')} rules={[yupSync]}>
          <PartySearch disabled={isOrbis || addParticipant.isLoading} />
        </Form.Item>
        <Form.Item<IFormValues>
          name="contract_number"
          required
          label={spaceT.t('coalition.contract')}
          rules={[yupSync]}
        >
          <Input placeholder={spaceT.t('coalition.contractPlaceholder')} />
        </Form.Item>
        <Form.Item<IFormValues>
          name="contract_date"
          required
          label={spaceT.t('coalition.contractDate')}
          normalize={(value) => (value ? value.format('YYYY-MM-DD') : undefined)}
          getValueProps={(value) => ({ value: value ? dayjs(value) : null })}
          rules={[yupSync]}
        >
          <DatePicker format="DD.MM.YYYY" />
        </Form.Item>
        {formError && <Alert type="error" showIcon title={formError} />}
      </Form>
    </Modal>
  );
};
